import { useMemo } from "react";
import { AlertTriangle, AlertCircle, CheckCircle2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ProcessedFile } from "./InvoiceList";
import type { CompanyMaster } from "@/lib/supplierMaster";
import { isValidTaxIdForExport } from "@/lib/spanishNif";
import { formatNumber, parseNumericLike } from "@/lib/numberFormatting";

export type ValidationSeverity = "error" | "warning";

export interface ValidationIssue {
  id: string;
  fileId: string;
  severity: ValidationSeverity;
  field: string;
  message: string;
  /** Etiqueta corta de la factura para localizarla */
  invoiceLabel: string;
}

interface PreExportValidatorProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Si tiene errores críticos, no se permite continuar. */
  files: ProcessedFile[];
  appliedCompany: CompanyMaster | null;
  /** Se invoca al pulsar "Ir a la fila" para abrir esa factura. */
  onFocusFile?: (fileId: string) => void;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export const collectValidationIssues = (
  files: ProcessedFile[],
  appliedCompany: CompanyMaster | null,
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  // Empresa: código A3 obligatorio (error crítico, bloquea)
  if (!appliedCompany) {
    issues.push({
      id: "company-missing",
      fileId: "",
      severity: "error",
      field: "empresa",
      message: "No hay empresa seleccionada. Aplica un maestro antes de exportar.",
      invoiceLabel: "—",
    });
  } else if (!(appliedCompany.a3_company_code || "").replace(/\D/g, "")) {
    issues.push({
      id: "company-a3-code",
      fileId: "",
      severity: "error",
      field: "empresa",
      message: `La empresa "${appliedCompany.name}" no tiene "Código A3" configurado.`,
      invoiceLabel: "—",
    });
  }

  for (const f of files) {
    const label = `${f.supplier || "Proveedor s/n"} · ${f.invoiceNumber || "(s/n)"}`;

    // NIF inválido (crítico)
    const nif = (f.supplierTaxId || "").trim();
    if (!nif) {
      issues.push({
        id: `${f.id}-nif-empty`,
        fileId: f.id,
        severity: "error",
        field: "NIF",
        message: "Falta el NIF del proveedor.",
        invoiceLabel: label,
      });
    } else if (!isValidTaxIdForExport(nif)) {
      issues.push({
        id: `${f.id}-nif-invalid`,
        fileId: f.id,
        severity: "error",
        field: "NIF",
        message: `NIF inválido o con dígito de control incorrecto: "${nif}". Para proveedores extranjeros, antepón el código de país (ej. "IE9852817J", "FR12345678901").`,
        invoiceLabel: label,
      });
    }

    // Cuenta de proveedor (crítico)
    if (!f.supplierAccountCode) {
      issues.push({
        id: `${f.id}-supplier-acc`,
        fileId: f.id,
        severity: "error",
        field: "Cuenta proveedor",
        message: "Falta la cuenta contable de proveedor.",
        invoiceLabel: label,
      });
    }

    // Cuenta de gasto (crítico salvo leasing con cuenta de recuperación)
    const isLeasing = !!(f.leasingCostRecovery && f.leasingCostRecovery !== 0);
    if (!isLeasing) {
      const breakdown = f.vatBreakdown && f.vatBreakdown.length > 0
        ? f.vatBreakdown
        : [{ taxableBase: 0, vatRate: 0, vatAmount: 0 }];
      const hasMissingExpense = breakdown.some((line, idx) => {
        const lineExpense = (line as { expenseAccount?: string }).expenseAccount;
        const expenseForRow = typeof lineExpense === "string"
          ? lineExpense
          : idx === 0
            ? (f.expenseAccountCode || "")
            : "";
        return !expenseForRow;
      });
      if (hasMissingExpense) {
        issues.push({
          id: `${f.id}-expense-acc`,
          fileId: f.id,
          severity: "error",
          field: "Cuenta gasto",
          message: "Falta la cuenta de gasto en al menos una línea de IVA.",
          invoiceLabel: label,
        });
      }
    }

    // Base + IVA - IRPF ≈ Total (advertencia, tolerancia 0,02 €)
    if (f.totalAmount !== undefined && f.vatBreakdown && f.vatBreakdown.length > 0) {
      const sumBase = f.vatBreakdown.reduce((s, l) => s + parseNumericLike(l.taxableBase), 0);
      const sumVat = f.vatBreakdown.reduce((s, l) => s + parseNumericLike(l.vatAmount), 0);
      const irpf = parseNumericLike(f.irpfAmount ?? 0);
      const expected = round2(sumBase + sumVat - irpf);
      const actual = round2(parseNumericLike(f.totalAmount));
      const diff = Math.abs(expected - actual);
      // No marcamos leasing (su total se compone con extras)
      if (!isLeasing && diff > 0.02) {
        issues.push({
          id: `${f.id}-balance`,
          fileId: f.id,
          severity: "warning",
          field: "Cuadre",
          message: `Base + IVA − IRPF (${formatNumber(expected)} €) no cuadra con el Total (${formatNumber(actual)} €). Diferencia ${formatNumber(diff)} €.`,
          invoiceLabel: label,
        });
      }
    }

    // Fecha contable
    if (!f.date) {
      issues.push({
        id: `${f.id}-date`,
        fileId: f.id,
        severity: "error",
        field: "Fecha",
        message: "Falta la fecha de la factura.",
        invoiceLabel: label,
      });
    }

    // Tipo de operación IVA — coherencia con NIF / cuota
    const op = f.operationType ?? "interior";
    if (op === "aib" || op === "ais") {
      const prefix = (f.supplierTaxId || "").trim().toUpperCase().slice(0, 2);
      const isEU = /^(AT|BE|BG|CY|CZ|DE|DK|EE|EL|GR|ES|FI|FR|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK|XI)$/.test(prefix);
      if (!isEU || prefix === "ES") {
        issues.push({
          id: `${f.id}-op-intracom-nif`,
          fileId: f.id,
          severity: "warning",
          field: "Tipo op.",
          message: `Operación marcada como ${op === "aib" ? "intracom. bienes" : "intracom. servicios"} pero el NIF "${f.supplierTaxId || "(s/n)"}" no lleva prefijo UE. Revisa para que el modelo 349 sea coherente.`,
          invoiceLabel: label,
        });
      }
    }
    if (op === "importacion") {
      const hasVat = (f.vatBreakdown || []).some(
        (l) => parseNumericLike(l.vatAmount) !== 0 || parseNumericLike(l.vatRate) !== 0,
      );
      if (hasVat) {
        issues.push({
          id: `${f.id}-op-importacion-vat`,
          fileId: f.id,
          severity: "warning",
          field: "Tipo op.",
          message: "Operación marcada como Importación (DUA) con cuota IVA > 0. La cuota suele liquidarla la aduana en el DUA — verifica que no estés duplicando.",
          invoiceLabel: label,
        });
      }
    }
  }

  return issues;
};

const PreExportValidator = ({
  open,
  onClose,
  onConfirm,
  files,
  appliedCompany,
  onFocusFile,
}: PreExportValidatorProps) => {
  const issues = useMemo(
    () => collectValidationIssues(files, appliedCompany),
    [files, appliedCompany],
  );

  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");
  const hasBlocking = errors.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {issues.length === 0 ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                Todo listo para exportar
              </>
            ) : hasBlocking ? (
              <>
                <AlertCircle className="h-5 w-5 text-destructive" />
                Revisa antes de exportar
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Avisos antes de exportar
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {issues.length === 0
              ? `${files.length} factura${files.length === 1 ? "" : "s"} validada${files.length === 1 ? "" : "s"}. Pulsa "Generar SUENLACE" para descargar el fichero.`
              : `${errors.length} error${errors.length === 1 ? "" : "es"} crítico${errors.length === 1 ? "" : "s"} y ${warnings.length} aviso${warnings.length === 1 ? "" : "s"}. Los errores críticos impiden la exportación.`}
          </DialogDescription>
        </DialogHeader>

        {issues.length > 0 && (
          <ScrollArea className="max-h-[55vh] pr-3">
            <div className="space-y-2">
              {[...errors, ...warnings].map((issue) => (
                <div
                  key={issue.id}
                  className={`flex items-start justify-between gap-3 rounded-md border p-3 ${
                    issue.severity === "error"
                      ? "border-destructive/30 bg-destructive/5"
                      : "border-amber-300/40 bg-amber-50 dark:bg-amber-950/20"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <Badge variant={issue.severity === "error" ? "destructive" : "secondary"}>
                        {issue.severity === "error" ? "Error" : "Aviso"}
                      </Badge>
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {issue.field}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {issue.invoiceLabel}
                      </span>
                    </div>
                    <p className="text-sm leading-snug">{issue.message}</p>
                  </div>
                  {issue.fileId && onFocusFile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        onFocusFile(issue.fileId);
                        onClose();
                      }}
                    >
                      Ir a la fila
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            <X className="mr-1 h-4 w-4" />
            Cancelar
          </Button>
          <Button
            onClick={onConfirm}
            disabled={hasBlocking}
            title={hasBlocking ? "Resuelve los errores críticos para continuar" : undefined}
          >
            {warnings.length > 0 && !hasBlocking ? "Generar igualmente" : "Generar SUENLACE"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PreExportValidator;
